import React, { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import DraggableFloater from "./DraggableFloater";

/**
 * ArmLiveTradingButton — owner-facing big toggle that hits the LIVE
 * Capital.com auto-trader. Default OFF. The owner can click ARM whenever
 * they want; voice command "stop auto-trading" also disarms. Drag-able
 * so it can be parked anywhere on screen.
 *
 * Owner brief (Feb 2026): "Make sure there's a clear of trading button
 * that I press I don't want it linked to a demo I want it linked to
 * live but when I'm ready, I'll just click on and then it'll be ready
 * to go".
 */
export default function ArmLiveTradingButton() {
  const [armed, setArmed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [stats, setStats] = useState({ open_positions: 0, spent_today_gbp: 0, daily_cap_gbp: 200 });

  const reload = useCallback(async () => {
    try {
      const r = await api.get("/trading/auto_settings");
      setArmed(!!r.data?.armed);
      setStats({
        open_positions:   r.data?.open_positions   ?? 0,
        spent_today_gbp:  r.data?.spent_today_gbp  ?? 0,
        daily_cap_gbp:    r.data?.daily_cap_gbp    ?? 200,
      });
    } catch (_) { /* ignore */ }
  }, []);

  useEffect(() => { reload(); }, [reload]);
  useEffect(() => {
    const id = setInterval(reload, 15000);
    return () => clearInterval(id);
  }, [reload]);

  const flip = async (e) => {
    e?.stopPropagation?.();
    if (busy) return;
    if (!armed) {
      const ok = window.confirm(
        "Arm LIVE Capital.com auto-trader?\n\n" +
        "• £50 per trade · £200 daily cap\n" +
        "• Max 5 open positions\n" +
        "• 15-pip stop · 15-pip take-profit\n" +
        "• REAL MONEY on your live broker\n\n" +
        "Voice command 'stop auto-trading' will disarm instantly."
      );
      if (!ok) return;
    }
    setBusy(true);
    try {
      await api.post(armed ? "/trading/auto_stop" : "/trading/auto_start");
      await reload();
    } finally { setBusy(false); }
  };

  return (
    <DraggableFloater
      storageKey="rawco-arm-live-pos"
      initial={{ left: 12, bottom: 14 }}
      zIndex={48}
      testid="arm-live-floater"
    >
      <button
        type="button"
        onClick={flip}
        disabled={busy}
        className={`arm-live-btn ${armed ? "armed" : "disarmed"}`}
        data-testid="arm-live-trading-btn"
      >
        <span className="arm-live-pulse"/>
        <div className="arm-live-text">
          <div className="arm-live-state">
            {busy ? "…" : armed ? "● LIVE TRADING ARMED" : "○ ARM LIVE TRADING"}
          </div>
          <div className="arm-live-meta">
            {armed
              ? `${stats.open_positions} open · £${(stats.spent_today_gbp || 0).toFixed(0)}/£${stats.daily_cap_gbp} today`
              : "Tap when ready · Capital.com live · £50/trade · 15/15 pip"}
          </div>
        </div>
      </button>

      <style>{`
        .arm-live-btn {
          position: relative;
          display: flex; align-items: center; gap: 14px;
          padding: 12px 22px;
          border-radius: 999px;
          border: 2.5px solid;
          cursor: pointer;
          font-family: inherit;
          transition: all 200ms ease;
          backdrop-filter: blur(10px);
          white-space: nowrap;
        }
        .arm-live-btn.disarmed {
          background: linear-gradient(135deg, rgba(20,40,30,0.85), rgba(10,30,20,0.85));
          border-color: #10b981;
          color: #6ee7b7;
          box-shadow: 0 0 20px rgba(16,185,129,0.35), 0 0 50px rgba(16,185,129,0.15);
        }
        .arm-live-btn.armed {
          background: linear-gradient(135deg, rgba(60,15,15,0.92), rgba(40,8,8,0.92));
          border-color: #ef4444;
          color: #fca5a5;
          box-shadow: 0 0 30px rgba(239,68,68,0.55), 0 0 70px rgba(239,68,68,0.25);
          animation: arm-live-flash 1.6s ease-in-out infinite;
        }
        .arm-live-btn:hover:not(:disabled) {
          transform: scale(1.04);
          filter: brightness(1.15);
        }
        .arm-live-btn:active { transform: scale(0.97); }
        .arm-live-btn:disabled { opacity: 0.6; cursor: wait; }

        .arm-live-pulse {
          width: 14px; height: 14px;
          border-radius: 50%;
          flex-shrink: 0;
        }
        .arm-live-btn.disarmed .arm-live-pulse {
          background: #10b981;
          box-shadow: 0 0 10px #10b981;
        }
        .arm-live-btn.armed .arm-live-pulse {
          background: #ef4444;
          box-shadow: 0 0 14px #ef4444, 0 0 24px #ef4444;
          animation: arm-live-blink 0.9s ease-in-out infinite;
        }
        .arm-live-state {
          font-size: 14px;
          font-weight: 700;
          letter-spacing: 1.5px;
          line-height: 1.1;
        }
        .arm-live-meta {
          font-size: 9px;
          opacity: 0.75;
          letter-spacing: 1px;
          margin-top: 3px;
        }
        @keyframes arm-live-blink {
          0%,100% { opacity: 1; }
          50%     { opacity: 0.5; }
        }
        @keyframes arm-live-flash {
          0%,100% { box-shadow: 0 0 30px rgba(239,68,68,0.55), 0 0 70px rgba(239,68,68,0.25); }
          50%     { box-shadow: 0 0 44px rgba(239,68,68,0.85), 0 0 100px rgba(239,68,68,0.4); }
        }
      `}</style>
    </DraggableFloater>
  );
}
